import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { formatDistanceToNow } from "date-fns";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Battery, Power, PowerOff, Loader, Info, Shield, HardDrive, Camera, TrendingUp } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUser } from "@supabase/auth-helpers-react";
import { getFormattedDate } from "@/utils/deviceUtils";
import { supabase } from "@/integrations/supabase/client";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import { GroupSelector } from "@/components/devices/GroupSelector";
import { useDeviceGroups, useAssignDeviceToGroup } from "@/hooks/use-device-groups";
import { useCrossOrgAssignmentCheck } from "@/hooks/use-cross-org-assignment-check";
import { CrossOrgAssignmentDialog } from "@/components/devices/CrossOrgAssignmentDialog";
import { ContractStatusCard } from "@/components/devices/ContractStatusCard";
import { SolarPerformanceChart } from "@/components/devices/SolarPerformanceChart";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";
import { useDeviceOrganizationName } from "@/hooks/use-device-organization-name";

export const DeviceOverview = ({
  device
}: {
  device: any;
}) => {
  const [localName, setLocalName] = useState(device.deviceName || "");
  const [localDescription, setLocalDescription] = useState(device.deviceDescription || "");
  const [localGroupName, setLocalGroupName] = useState(device.groupName || "");
  const [localGroupReference, setLocalGroupReference] = useState(device.groupReference || "");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(device.group_id || null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRequestingPhoto, setIsRequestingPhoto] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [crossOrgDialogOpen, setCrossOrgDialogOpen] = useState(false);
  const { logChange } = useDeviceChangeLog();
  const [deviceOrganizationId, setDeviceOrganizationId] = useState<string | null>(null);
  const [showSolarChart, setShowSolarChart] = useState(false);
  const [pendingGroupAssignment, setPendingGroupAssignment] = useState<{
    groupId: string | null;
    groupName: string | null;
    deviceOrgName: string | null;
    groupOrgName: string | null;
  } | null>(null);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const user = useUser();
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const { data: groups = [] } = useDeviceGroups();
  const assignDeviceToGroup = useAssignDeviceToGroup();
  const { checkCrossOrgAssignment, getDeviceOrganizationId, isSuperuser } = useCrossOrgAssignmentCheck();
  const { data: assignedOrg } = useDeviceOrganizationName(device.deviceID);

  // Fetch the camera's organisation so the group list shows only its groups
  useEffect(() => {
    if (device.deviceID) {
      getDeviceOrganizationId(device.deviceID).then(setDeviceOrganizationId);
    }
  }, [device.deviceID, getDeviceOrganizationId]);


  // Check if Live Photo should be visible - visible to all users when device supports it
  const canTakeLivePhoto = device.deviceStatus === "RecordOnDemand";
  console.log("Live Photo availability:", {
    deviceStatus: device.deviceStatus,
    canTakeLivePhoto,
    userId: user?.id,
    userAuthenticated: !!user
  });

  const updateField = async (field: string, value: string) => {
    setIsUpdating(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", device.deviceID);
      params.append("field", field);
      params.append("value", value);
      console.log("Updating device with params:", Object.fromEntries(params));
      logChange(device.deviceID, field, value);
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Server error:", errorData);
        throw new Error(errorData.message || "Failed to update device");
      }
      queryClient.invalidateQueries({
        queryKey: ["device-details", device.deviceID]
      });
      queryClient.invalidateQueries({
        queryKey: ["device", device.deviceID]
      });
      toast.success("Device updated successfully");
    } catch (error) {
      console.error("Error updating device:", error);
      toast.error("Failed to update device");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLivePhoto = async () => {
    console.log("Live photo button clicked");
    setIsRequestingPhoto(true);
    
    try {
      // Get current user from Supabase
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser?.id) {
        console.error("No user found");
        toast.error("Authentication required. Please log in and try again.");
        return;
      }

      const params = new URLSearchParams();
      params.append("deviceID", device.deviceID);
      params.append("field", "userID");
      params.append("value", currentUser.id);
      
      console.log("Calling on-demand capture API with params:", Object.fromEntries(params));
      
      const response = await fetch("https://i56o7w.buildship.run/update-device-ondemand-capture", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      });

      console.log("API response status:", response.status);
      console.log("API response ok:", response.ok);

      // Check if the HTTP status is 200 (success)
      if (response.status === 200) {
        const result = await response.json();
        console.log("API response body:", result);

        if (result === true) {
          toast.success("Live photo requested successfully! It should be available in a couple of minutes.");
        } else {
          console.log("API returned false, request may have failed");
          toast.error("Failed to request live photo. Please try again.");
        }
      } else {
        // Handle non-200 status codes
        console.error("API returned non-200 status:", response.status);
        const errorText = await response.text();
        console.error("Error response:", errorText);
        toast.error("Failed to request live photo. Server error occurred.");
      }
    } catch (error) {
      console.error("Error calling live photo API:", error);
      toast.error("Failed to request live photo. Network error occurred.");
    } finally {
      setIsRequestingPhoto(false);
    }
  };

  // Helper function to perform the actual group assignment
  const performGroupAssignment = async (
    groupId: string | null,
    groupName: string | null,
    groupReference: string
  ) => {
    setSelectedGroupId(groupId);
    setLocalGroupName(groupName || "");
    setLocalGroupReference(groupReference);
    
    try {
      // Update database with group_id and group_name
      await assignDeviceToGroup.mutateAsync({
        deviceId: device.deviceID,
        groupId,
        groupName
      });
      
      // Also update BuildShip API with the group name string
      if (groupName !== device.groupName) {
        await updateField("groupName", groupName || "");
      }
      
      toast.success("Group updated successfully");
    } catch (error) {
      console.error("Error updating group:", error);
    }
  };

  // Determine which image URL to use based on device type
  const imageUrl = isMobile && device.imageKit?.thumbnailUrl ? device.imageKit.thumbnailUrl : device.imageKit?.url;
  
  return <div className="p-2">
      {/* Header */}
      <div className={`flex items-center justify-between mb-4 ${isMobile ? 'flex-col space-y-3' : ''}`}>
        <div className="flex items-center">
          <Info className="h-5 w-5 text-brand-500 mr-2" />
          <h2 className="text-lg font-semibold">Device Overview</h2>
        </div>
      </div>

      {/* Read-only banner */}
      {!canEditDeviceConfig && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Read-only Mode</AlertTitle>
          <AlertDescription>
            Only administrators can modify device information. Contact your admin to make changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Device Information Card */}
      <Card className="border bg-muted/50">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-brand-500" />
              <CardTitle className="text-base">Device Information</CardTitle>
            </div>
            {canTakeLivePhoto && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Take Live Photo</span>
                <TooltipProvider>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="secondary" 
                        size="icon" 
                        onClick={handleLivePhoto}
                        disabled={isRequestingPhoto}
                        aria-label="Take live photo"
                        className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
                      >
                        {isRequestingPhoto ? (
                          <Loader className="h-4 w-4 text-white animate-spin" strokeWidth={2.5} />
                        ) : (
                          <Camera className="h-4 w-4 text-white" strokeWidth={2.5} />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{isRequestingPhoto ? "Requesting live photo..." : "Request a new image from this device"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
            {/* Left column */}
            <div className="space-y-4">
              {imageUrl && <div className="relative overflow-hidden rounded-lg mb-4">
                  <img src={imageUrl} alt={device.deviceName || "Device image"} className="w-full h-full object-contain cursor-pointer" onClick={() => setShowImageDialog(true)} />
                </div>}
              
              {/* Content Safety Card hidden - kept for future use */}
              {false && !isMobile && device.safeSearch && Object.keys(device.safeSearch).length > 0 && <Card className="bg-muted/30 shadow-sm">
                  <CardHeader className="py-3">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-brand-500" />
                      <h3 className="font-medium text-sm">Content Safety</h3>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-0 text-xs">
                      {Object.entries(device.safeSearch).slice(0, 4).map(([key, value]) => <div key={key} className="flex justify-between space-x-2 px-2 py-1 md:px-4">
                          <span className="text-muted-foreground capitalize">{key}:</span>
                          <span className="font-medium capitalize">{String(value)}</span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>}
            </div>
            
            {/* Right column */}
            <div className="space-y-4">
              <div className="space-y-2">
                <dt className="font-medium text-sm flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-brand-500"></span>
                  Name
                </dt>
                <dd className="flex gap-2">
                  <Input value={localName} onChange={e => setLocalName(e.target.value)} placeholder="Enter device name" className="h-9 text-sm" disabled={isUpdating || !canEditDeviceConfig} />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button variant="outline" onClick={() => updateField("deviceName", localName)} className="h-9 text-sm" disabled={isUpdating || !canEditDeviceConfig}>
                            Update
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canEditDeviceConfig && (
                        <TooltipContent>
                          <p>Only administrators can edit device settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </dd>
              </div>
              
              <div className="space-y-2">
                <dt className="font-medium text-sm flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-brand-500"></span>
                  Description
                </dt>
                <dd className="flex gap-2">
                  <Textarea value={localDescription} onChange={e => setLocalDescription(e.target.value)} placeholder="Enter device description" className="min-h-[60px] max-h-[70px] text-sm" disabled={isUpdating || !canEditDeviceConfig} />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button variant="outline" onClick={() => updateField("deviceDescription", localDescription)} className="h-9 text-sm" disabled={isUpdating || !canEditDeviceConfig}>
                            Update
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canEditDeviceConfig && (
                        <TooltipContent>
                          <p>Only administrators can edit device settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </dd>
              </div>

              <div className="space-y-2">
                <dt className="font-medium text-sm flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-brand-500"></span>
                  Group
                </dt>
                <dd className="flex gap-2 items-center">
                  <div className="flex-1">
                    <GroupSelector
                      value={selectedGroupId}
                      deviceOrganizationId={deviceOrganizationId}
                      onChange={async (groupId, groupName) => {
                        // Find group reference if a group is selected
                        const selectedGroup = groups.find(g => g.id === groupId);
                        
                        // For superusers, check if this is a cross-org assignment
                        if (isSuperuser && groupId) {
                          const { isBlocked, orgInfo } = await checkCrossOrgAssignment(
                            device.deviceID,
                            groupId
                          );
                          
                          if (isBlocked && orgInfo) {
                            // Store org info and show blocking dialog (no assignment will be made)
                            setPendingGroupAssignment({
                              groupId,
                              groupName,
                              deviceOrgName: orgInfo.deviceOrgName,
                              groupOrgName: orgInfo.groupOrgName,
                            });
                            setCrossOrgDialogOpen(true);
                            return;
                          }
                        }
                        
                        // Proceed with assignment
                        await performGroupAssignment(groupId, groupName, selectedGroup?.reference || "");
                      }}
                      disabled={isUpdating || !canEditDeviceConfig}
                      showCreateButton={canEditDeviceConfig}
                    />
                  </div>
                  {!canEditDeviceConfig && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-muted-foreground">Read-only</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Only administrators can edit device settings</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </dd>
              </div>

              <div className="space-y-2">
                <dt className="font-medium text-sm flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-brand-500"></span>
                  Group Reference
                </dt>
                <dd>
                  <Input 
                    value={localGroupReference} 
                    placeholder="Set via group selection"
                    className="h-9 text-sm" 
                    disabled={true}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Reference is set automatically from the selected group
                  </p>
                </dd>
              </div>

              {/* Device Identification Card */}
              <Card className="bg-muted/30 shadow-sm">
                <CardHeader className="py-3">
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-4 w-4 text-brand-500" />
                    <h3 className="font-medium text-sm">Device Identification</h3>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Assigned Organisation:</span>
                      <span className="font-medium break-all text-right">
                        {assignedOrg?.organizationName || 'Unassigned'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Serial Number:</span>
                      <span className="font-medium break-all">
                        {device.deviceSerial || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Device ID:</span>
                      <span className="font-medium break-all">
                        {device.deviceID || 'N/A'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contract Status Card */}
              <ContractStatusCard deviceId={device.deviceID} />

              {/* Status Cards now under Device Identification card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Card className="bg-muted/30 shadow-sm">
                  <CardHeader className="py-3">
                    <div className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-brand-500" />
                      <h3 className="font-medium text-sm">System Status</h3>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className="font-medium">{device.deviceStatus || 'Unknown'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Updated:</span>
                        <span className="font-medium">
                          {getFormattedDate(device.deviceLastUpdated)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Firmware:</span>
                        <span className="font-medium">{device.deviceFirmware || 'Unknown'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted/30 shadow-sm">
                  <CardHeader className="py-3">
                    <div className="flex items-center gap-2">
                      <Battery className="h-4 w-4 text-brand-500" />
                      <h3 className="font-medium text-sm">Battery Status</h3>
                      <Button variant="outline" size="icon" className="h-6 w-6 ml-auto bg-white dark:bg-white/90" onClick={() => setShowSolarChart(true)}>
                        <TrendingUp className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Battery Level:</span>
                        <span className="font-medium">
                          {device.battery?.batterySoC || '0'}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Battery Voltage:</span>
                        <span className="font-medium">
                          {device.battery?.batteryVoltage || '0'}V
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Charge Voltage:</span>
                        <span className="font-medium">
                          {device.battery?.chargeVin || '0'}V
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Content Safety Card hidden - kept for future use */}
          {false && isMobile && device.safeSearch && Object.keys(device.safeSearch).length > 0 && (
            <Card className="bg-muted/30 shadow-sm mt-6">
              <CardHeader className="py-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-brand-500" />
                  <h3 className="font-medium text-sm">Content Safety</h3>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 gap-0 text-xs">
                  {Object.entries(device.safeSearch).slice(0, 4).map(([key, value]) => (
                    <div key={key} className="flex justify-between space-x-2 px-2 py-1 md:px-4">
                      <span className="text-muted-foreground capitalize">{key}:</span>
                      <span className="font-medium capitalize">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>


      {/* Image Dialog - Redesigned */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-3xl flex flex-col p-0 rounded-lg">
          <DialogHeader className="p-4 space-y-1">
            <DialogTitle className="text-lg">
              {device.deviceName || "Device Image"}
            </DialogTitle>
          </DialogHeader>
          {imageUrl && <div className="flex items-center justify-center p-4 pb-6 bg-black/5">
              <Zoom>
                <img src={imageUrl} alt={device.deviceName || "Device image"} className="max-w-full max-h-[60vh] object-contain rounded-md cursor-zoom-in" />
              </Zoom>
            </div>}
          
          <div className="p-4 bg-muted/20">
            <div className="flex flex-wrap justify-between text-sm gap-4">
              <div>
                <p className="font-medium">{device.deviceName || "Unnamed Device"}</p>
                {device.deviceDescription && <p className="text-muted-foreground mt-1">{device.deviceDescription}</p>}
              </div>
              <div className="text-right">
                <p className="text-muted-foreground">Last updated: {getFormattedDate(device.deviceLastUpdated)}</p>
                <p className="text-muted-foreground mt-1">Status: {device.deviceStatus || "Unknown"}</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CrossOrgAssignmentDialog
        open={crossOrgDialogOpen}
        onOpenChange={(open) => {
          setCrossOrgDialogOpen(open);
          if (!open) {
            // Clear the pending assignment - no action is taken (blocked)
            setPendingGroupAssignment(null);
          }
        }}
        deviceOrgName={pendingGroupAssignment?.deviceOrgName || null}
        groupOrgName={pendingGroupAssignment?.groupOrgName || null}
      />
      <SolarPerformanceChart
        deviceId={device.device_id}
        open={showSolarChart}
        onOpenChange={setShowSolarChart}
      />
    </div>;
};
